"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_MOTION_SCOPE_CLASS = exports.MOTION_INDEX_CSS_VAR = exports.MOTION_KEYFRAME_PREFIX = exports.MOTION_CLASS_PREFIX = void 0;
exports.getMotionClassName = getMotionClassName;
exports.renderMotionCss = renderMotionCss;
exports.MOTION_CLASS_PREFIX = 'sl-app-motion';
exports.MOTION_KEYFRAME_PREFIX = 'sl-app-kf';
exports.MOTION_INDEX_CSS_VAR = '--sl-app-motion-index';
exports.DEFAULT_MOTION_SCOPE_CLASS = 'sl-app';
const EASING_VALUES = {
    linear: 'linear',
    ease: 'ease',
    'ease-in': 'ease-in',
    'ease-out': 'ease-out',
    'ease-in-out': 'ease-in-out',
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1.2)',
};
const KEYFRAMES = {
    'fade-in': 'from { opacity: 0; } to { opacity: 1; }',
    'slide-up': 'from { opacity: 0; transform: translate3d(0, 16px, 0); } to { opacity: 1; transform: translate3d(0, 0, 0); }',
    'slide-down': 'from { opacity: 0; transform: translate3d(0, -16px, 0); } to { opacity: 1; transform: translate3d(0, 0, 0); }',
    'slide-left': 'from { opacity: 0; transform: translate3d(16px, 0, 0); } to { opacity: 1; transform: translate3d(0, 0, 0); }',
    'slide-right': 'from { opacity: 0; transform: translate3d(-16px, 0, 0); } to { opacity: 1; transform: translate3d(0, 0, 0); }',
    'scale-in': 'from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); }',
    'blur-in': 'from { opacity: 0; filter: blur(8px); } to { opacity: 1; filter: blur(0); }',
    reveal: 'from { opacity: 0; clip-path: inset(0 0 100% 0); } to { opacity: 1; clip-path: inset(0 0 0 0); }',
};
function getMotionClassName(spec) {
    return `${exports.MOTION_CLASS_PREFIX}-${hashMotionId(spec.id)}`;
}
function renderMotionCss(specs, options = {}) {
    const scopeClass = sanitizeScopeClass(options.scopeClass ?? exports.DEFAULT_MOTION_SCOPE_CLASS);
    const rules = [];
    const classMap = {};
    let needsReducedFadeKeyframes = false;
    for (const spec of specs) {
        classMap[spec.id] = getMotionClassName(spec);
        if (spec.preset === 'none' || spec.trigger === 'none')
            continue;
        const rendered = renderSpecCss(spec, scopeClass);
        rules.push(rendered.css);
        needsReducedFadeKeyframes =
            needsReducedFadeKeyframes || rendered.needsReducedFadeKeyframes;
    }
    if (needsReducedFadeKeyframes) {
        rules.push(`@keyframes ${exports.MOTION_KEYFRAME_PREFIX}-reduced-fade { from { opacity: 0; } to { opacity: 1; } }`);
    }
    return { css: rules.join('\n\n'), classMap };
}
function renderSpecCss(spec, scopeClass) {
    const className = getMotionClassName(spec);
    if (spec.preset === 'none') {
        return { css: '', needsReducedFadeKeyframes: false };
    }
    const keyframeName = `${exports.MOTION_KEYFRAME_PREFIX}-${hashMotionId(`${spec.id}:${spec.preset}`)}`;
    const baseDelay = spec.staggerMs === 0
        ? `${spec.delayMs}ms`
        : `calc(${spec.delayMs}ms + (var(${exports.MOTION_INDEX_CSS_VAR}, 0) * ${spec.staggerMs}ms))`;
    const blocks = [
        `@keyframes ${keyframeName} { ${KEYFRAMES[spec.preset]} }`,
        `.${scopeClass} .${className} { animation-name: ${keyframeName}; animation-duration: ${spec.durationMs}ms; animation-delay: ${baseDelay}; animation-timing-function: ${EASING_VALUES[spec.easing]}; animation-fill-mode: both; }`,
    ];
    const reducedMotion = renderReducedMotionCss(spec, scopeClass, className);
    if (reducedMotion.css)
        blocks.push(reducedMotion.css);
    return {
        css: blocks.join('\n'),
        needsReducedFadeKeyframes: reducedMotion.needsReducedFadeKeyframes,
    };
}
function renderReducedMotionCss(spec, scopeClass, className) {
    if (spec.reducedMotionPolicy === 'preserve') {
        return { css: '', needsReducedFadeKeyframes: false };
    }
    const selector = `.${scopeClass} .${className}`;
    if (spec.reducedMotionPolicy === 'static') {
        return {
            css: `@media (prefers-reduced-motion: reduce) { ${selector} { animation: none; opacity: 1; transform: none; filter: none; clip-path: none; } }`,
            needsReducedFadeKeyframes: false,
        };
    }
    return {
        css: `@media (prefers-reduced-motion: reduce) { ${selector} { animation-name: ${exports.MOTION_KEYFRAME_PREFIX}-reduced-fade; animation-duration: 120ms; animation-delay: 0ms; animation-timing-function: ease-out; } }`,
        needsReducedFadeKeyframes: true,
    };
}
function sanitizeScopeClass(scopeClass) {
    if (!/^[a-z][a-z0-9-]*$/i.test(scopeClass)) {
        throw new Error(`Invalid motion scope class: ${scopeClass}`);
    }
    return scopeClass;
}
function hashMotionId(value) {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}
